import React, { memo, useState } from "react";
import classes from "./ImageView.module.css";
import ImgFrame from "../../../components/common/ImageFrame/ImgFrame";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { ArrowLeft, ArrowRight, Close, Download } from "@mui/icons-material";
import { Document, Page, pdfjs } from "react-pdf";
import { Box, Button, Modal } from "@mui/material";
import noImageAvailable from "../../../assets/images/png/no-image-available.png";
import { saveAs } from "file-saver";
import { configurePdfWorker } from "../../../utils/configurePdfWorker";
import {
  getUrlFileExtension,
  isImageUrl,
  isPdfUrl,
  resolveMediaUrl,
} from "../../../utils/mediaUrl";

configurePdfWorker(pdfjs);

const ImageView = ({
  customClass,
  imgCustomClass,
  imgSrc,
  label,
  viewClick,
  imgFieldClass,
  widthSize,
}) => {
  // view image states
  const [documentImage, setDocumentImage] = useState();
  const [openImageFullView, setOpenImageFullView] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [loadError, setLoadError] = useState("");

  const normalizedSrc = resolveMediaUrl(imgSrc);
  const extension = getUrlFileExtension(normalizedSrc);
  const fileName = normalizedSrc ? normalizedSrc.split("/").pop()?.split("?")[0] : "";

  const handleOpenImageFullView = (imageSrc) => {
    setOpenImageFullView(true);
    setLoadError("");
    const replacedUrl = resolveMediaUrl(imageSrc);
    setDocumentImage(replacedUrl || noImageAvailable);
    if (isPdfUrl(replacedUrl)) {
      setCurrentPage(1);
      setNumPages(null);
    }
  };

  async function downloadimg(url, fileName) {
    const replacedUrl = resolveMediaUrl(url);
    const a = document.createElement("a");
    a.href = await toDataURL(replacedUrl);
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  function toDataURL(url) {
    const replacedUrl = resolveMediaUrl(url);
    return fetch(replacedUrl)
      .then((response) => {
        return response.blob();
      })
      .then((blob) => {
        return URL.createObjectURL(blob);
      });
  }

  function DownloadImage(src, fileName) {
    downloadimg(src, fileName);
  }

  const closeViewImagePopup = () => {
    setOpenImageFullView(false);
    setLoadError("");
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < numPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const renderPreview = () => {
    if (!normalizedSrc) {
      return <ImgFrame imgSrc={noImageAvailable} customClass={imgCustomClass} />;
    }

    if (isPdfUrl(normalizedSrc)) {
      // Match UAT-style viewing: use Google viewer for PDFs.
      const googleViewerSrc = `https://docs.google.com/gview?url=${encodeURIComponent(normalizedSrc)}&embedded=true`;
      return (
        <div style={{ width: "100%" }}>
          <iframe
            title={fileName || "PDF Viewer"}
            src={googleViewerSrc}
            style={{
              width: "100%",
              height: "240px",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              background: "#fff",
            }}
          />
        </div>
      );
    }

    if (isImageUrl(normalizedSrc)) {
      return <ImgFrame imgSrc={normalizedSrc} customClass={imgCustomClass} />;
    }

    return <ImgFrame imgSrc={noImageAvailable} customClass={imgCustomClass} />;
  };

  return (
    <div
      className={`${classes?.imgViewWrapper || ""} ${customClass || ""}`}
      style={{
        width: widthSize || "100px",
      }}
    >
      <div className={`${classes.imgField} ${imgFieldClass}`}>
        <RemoveRedEyeIcon
          className={classes.viewfullImageIcon}
          onClick={() => {
            handleOpenImageFullView(imgSrc);
          }}
        />
        <Download
          className={classes.downloadFileIcon}
          sx={{
            fontSize: "1.8rem",
          }}
          onClick={() => {
            DownloadImage(imgSrc, fileName)
          }}
        />
        {renderPreview()}
      </div>
      <p className={classes.label}>{label}</p>

      {/* view full image popup */}
      <Modal
        open={openImageFullView}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            outline: "none",
            maxWidth: "90vw",
            maxHeight: "90vh",
          }}
        >
          <div className={classes.fullImageViewWrap}>
            <Close
              className={classes.closeBtn}
              onClick={() => {
                closeViewImagePopup();
              }}
            />
            <div className={classes.fullImageView}>
              {isPdfUrl(normalizedSrc) ? (
                <div className={classes.pdfWrapper}>
                  <p>{fileName}</p>
                  <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "10px" }}>
                    <a
                      href={`https://docs.google.com/gview?url=${encodeURIComponent(normalizedSrc)}&embedded=true`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open PDF in new tab
                    </a>
                    <a href={normalizedSrc} target="_blank" rel="noreferrer">
                      Open original file
                    </a>
                  </div>
                  <iframe
                    title={fileName || "PDF Viewer"}
                    src={`https://docs.google.com/gview?url=${encodeURIComponent(normalizedSrc)}&embedded=true`}
                    style={{
                      width: "100%",
                      height: "70vh",
                      border: "1px solid #e5e7eb",
                      borderRadius: "12px",
                      background: "#fff",
                    }}
                  />
                  <div className={classes.pdfBtn}>
                    <p>
                      {/* Keep the same footer UI for consistency, but Google viewer handles paging/scrolling */}
                      {fileName}
                    </p>
                    <div className={classes.flex}>
                      <Button
                        className={classes.button}
                        onClick={handlePrevPage}
                        disabled={true}
                      >
                        <ArrowLeft
                          sx={{
                            fontSize: "2.4rem",
                            color: "#000",
                          }}
                        />
                      </Button>
                      <Button
                        className={classes.button}
                        onClick={handleNextPage}
                        disabled={true}
                      >
                        <ArrowRight
                          sx={{
                            fontSize: "2.4rem",
                            color: "#000",
                          }}
                        />
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <img
                  src={documentImage}
                  alt="Full View"
                  className={classes.fullImage}
                />
              )}
            </div>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default memo(ImageView);
